import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useHome } from "@/context/HomeContent";
import { mediaUrl, normalizeHref } from "@/lib/api";
import { fadeUp, staggerParent, viewportOnce } from "@/lib/animations";

const Solutions = () => {
  const { home } = useHome();
  const d = home.solutions;
  return (
    <section id="solutions" data-testid="solutions-section" className="bg-white py-24 sm:py-32">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <motion.div variants={staggerParent} initial="hidden" whileInView="show" viewport={viewportOnce} className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <motion.p variants={fadeUp} className="mb-5 font-heading text-xs font-bold uppercase tracking-[0.22em] text-brand-magenta">{d.eyebrow}</motion.p>
            <motion.h2 variants={fadeUp} className="font-heading text-4xl font-semibold leading-[1.05] tracking-tight text-brand-ink sm:text-5xl lg:text-[3.4rem]">
              {d.headingLead}{" "}
              <span className="brand-gradient-text">{d.headingAccent}</span>
            </motion.h2>
          </div>
          <motion.p variants={fadeUp} className="max-w-sm font-body text-base leading-relaxed text-brand-ink/60">{d.intro}</motion.p>
        </motion.div>

        <motion.div variants={staggerParent} initial="hidden" whileInView="show" viewport={viewportOnce} className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2">
          {(d.categories || []).map((cat, i) => (
            <motion.article key={i} variants={fadeUp} data-testid={`solution-card-${i}`} className="group flex flex-col overflow-hidden rounded-[26px] border border-brand-line bg-brand-cream transition-shadow duration-500 hover:shadow-2xl hover:shadow-brand-ink/5">
              <div className="relative h-[424px] overflow-hidden">
                <img src={mediaUrl(cat.image)} alt={cat.title} className="h-full w-full object-cover object-top transition-transform duration-[900ms] ease-out group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/60 to-transparent" />
                <span className="absolute left-5 top-5 rounded-full bg-white/90 px-3 py-1 font-heading text-xs font-bold tracking-widest text-brand-ink backdrop-blur">{cat.index}</span>
                <h3 className="absolute bottom-5 left-5 right-5 font-heading text-2xl font-semibold text-white sm:text-3xl">{cat.title}</h3>
              </div>
              <div className="flex flex-1 flex-col p-7 lg:p-8">
                <p className="font-body text-[15px] leading-relaxed text-brand-ink/70">{cat.desc}</p>
                <ul className="mt-6 flex flex-wrap gap-2">
                  {(cat.programs || []).map((p) => (
                    <li key={p} className="rounded-full border border-brand-line bg-white px-3 py-1.5 font-body text-[13px] text-brand-ink/75">{p}</li>
                  ))}
                </ul>
                <a href={normalizeHref(cat.linkHref) || "#featured"} data-testid={`solution-link-${i}`} className="group/link mt-7 inline-flex items-center gap-1.5 font-heading text-sm font-semibold text-brand-magenta">
                  {cat.linkLabel || "Explore Programs"}
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5" />
                </a>
              </div>
            </motion.article>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Solutions;
